import { Quote, Star } from 'lucide-react'

interface Testimonial {
  quote: string
  author: string
  title: string
  company: string
  rating: number
}

const testimonials: Testimonial[] = [
  {
    quote: "Clarantis transformed our compliance program. What used to take our team months of manual work now happens automatically. We passed our SOC 2 audit with zero findings.",
    author: "Sarah Chen",
    title: "VP of Security",
    company: "Fortune 500 Financial Services",
    rating: 5,
  },
  {
    quote: "The AI-powered gap analysis identified control weaknesses we'd been missing for years. Implementation was seamless, and our team was productive from day one.",
    author: "Michael Torres",
    title: "CISO",
    company: "Global Healthcare Organization",
    rating: 5,
  },
  {
    quote: "We evaluated five GRC platforms. Clarantis was the only one that delivered enterprise capability without enterprise complexity. Our auditors were impressed.",
    author: "Jennifer Williams",
    title: "Director of Compliance",
    company: "Technology Enterprise",
    rating: 5,
  },
]

const stats = [
  { value: '80%', label: 'Reduction in Audit Prep Time' },
  { value: '500+', label: 'Enterprise Clients' },
  { value: '99.9%', label: 'Platform Uptime' },
  { value: '24/7', label: 'Expert Support' },
]

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-1" aria-label={`${rating} out of 5 stars`}>
      {[...Array(5)].map((_, index) => (
        <Star
          key={index}
          className={`h-4 w-4 ${
            index < rating ? 'text-accent-teal fill-accent-teal' : 'text-navy-200'
          }`}
          aria-hidden="true"
        />
      ))}
    </div>
  )
}

export default function Testimonials() {
  return (
    <section id="testimonials" className="py-20 sm:py-28 bg-navy-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-accent-teal">
            Trusted by Leaders
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-primary-700 sm:text-4xl">
            What security teams are saying
          </p>
          <p className="mt-6 text-lg leading-8 text-navy-500">
            Organizations across industries trust Clarantis to simplify compliance and strengthen their security posture.
          </p>
        </div>

        {/* Stats bar */}
        <div className="mt-16 rounded-2xl bg-primary-700 p-8 sm:p-10">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-accent-teal">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-primary-100">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials grid */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="relative rounded-2xl bg-white p-8 shadow-sm ring-1 ring-navy-100 hover:shadow-md transition-shadow duration-200"
            >
              {/* Quote icon */}
              <div className="absolute -top-4 left-8">
                <div className="inline-flex items-center justify-center rounded-full bg-primary-700 p-3 shadow-md">
                  <Quote className="h-5 w-5 text-accent-teal" aria-hidden="true" />
                </div>
              </div>

              {/* Rating */}
              <div className="mt-4">
                <StarRating rating={testimonial.rating} />
              </div>

              {/* Quote text */}
              <blockquote className="mt-6">
                <p className="text-base leading-7 text-navy-600">
                  "{testimonial.quote}"
                </p>
              </blockquote>

              {/* Author info */}
              <div className="mt-8 flex items-center gap-4">
                {/* Avatar placeholder */}
                <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center text-white font-semibold text-lg">
                  {testimonial.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="font-semibold text-primary-700">
                    {testimonial.author}
                  </div>
                  <div className="text-sm text-navy-500">
                    {testimonial.title}
                  </div>
                  <div className="text-sm text-navy-400">
                    {testimonial.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges section */}
        <div className="mt-20 text-center">
          <p className="text-sm font-medium text-navy-400 uppercase tracking-wider mb-8">
            Compliance Certifications & Partnerships
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10">
            {/* Certification badges - styled as professional trust indicators */}
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm ring-1 ring-navy-100">
              <div className="h-12 w-12 rounded-full bg-primary-700/10 flex items-center justify-center">
                <span className="text-primary-700 font-bold text-sm">SOC 2</span>
              </div>
              <span className="text-xs text-navy-500">Type II Certified</span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm ring-1 ring-navy-100">
              <div className="h-12 w-12 rounded-full bg-primary-700/10 flex items-center justify-center">
                <span className="text-primary-700 font-bold text-xs">ISO</span>
              </div>
              <span className="text-xs text-navy-500">27001 Certified</span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm ring-1 ring-navy-100">
              <div className="h-12 w-12 rounded-full bg-primary-700/10 flex items-center justify-center">
                <span className="text-primary-700 font-bold text-xs">HIPAA</span>
              </div>
              <span className="text-xs text-navy-500">Compliant</span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm ring-1 ring-navy-100">
              <div className="h-12 w-12 rounded-full bg-primary-700/10 flex items-center justify-center">
                <span className="text-primary-700 font-bold text-xs">GDPR</span>
              </div>
              <span className="text-xs text-navy-500">Ready</span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm ring-1 ring-navy-100">
              <div className="h-12 w-12 rounded-full bg-primary-700/10 flex items-center justify-center">
                <span className="text-primary-700 font-bold text-xs">FedRAMP</span>
              </div>
              <span className="text-xs text-navy-500">In Progress</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
